import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Button from "components/ui/Button";
import { useGetOptionQuery } from "apis/communityTriggerOptions";

const capitalize = (str) => str && str.charAt(0).toUpperCase() + str.slice(1);

// Helper function to format initial data
const formatInitialData = (data) => {
  if (!data)
    return {
      trigger: "",
      type: "",
      vessel: [],
      message: "",
      receiver: [],
      expiry: "",
    };

  // Format vessel array
  const formattedVessel =
    data.vessel?.map((item) => {
      let optionId;
      if (item.optionId && typeof item.optionId === "object") {
        optionId = item.optionId._id;
      } else if (typeof item.optionId === "string") {
        optionId = item.optionId;
      } else if (typeof item === "string") {
        optionId = item;
      } else if (item._id) {
        optionId = item._id;
      }

      return {
        optionId: optionId,
        quantity: item.quantity || "",
      };
    }) || [];

  // Format receiver array
  const formattedReceiver =
    data.receiver?.map((item) => {
      let optionId;
      if (item.optionId && typeof item.optionId === "object") {
        optionId = item.optionId._id;
      } else if (typeof item.optionId === "string") {
        optionId = item.optionId;
      } else if (typeof item === "string") {
        optionId = item;
      } else if (item._id) {
        optionId = item._id;
      }

      return {
        optionId: optionId,
        quantity: item.quantity || "",
      };
    }) || [];

  return {
    trigger: data.trigger || "",
    type: data.type?._id || data.type || "",
    vessel: formattedVessel,
    message: data.message || "",
    prompt: data.prompt || "",
    receiver: formattedReceiver,
    expiry: data.expiry || "",
  };
};

const TemplateEdit = ({ initialData, onSubmit, tags }) => {
  console.log(initialData);
  const { data: optionsData, isLoading, isError, error } = useGetOptionQuery();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger: triggerValidation,
    formState: { errors },
  } = useForm({
    defaultValues: formatInitialData(initialData),
  });

  // Update form values when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      const formattedData = formatInitialData(initialData);
      Object.keys(formattedData).forEach((key) => {
        setValue(key, formattedData[key]);
      });
    }
  }, [initialData, setValue]);

  // Register vessel and receiver for validation
  useEffect(() => {
    register("vessel", {
      validate: (value) => {
        if (!value || value.length === 0) {
          return "At least one vessel option is required";
        }

        for (const item of value) {
          const option = optionsData?.find((opt) => opt._id === item.optionId);
          if (
            shouldShowQuantity(option?.name, "vessel") &&
            (!item.quantity || item.quantity === "")
          ) {
            return "Quantity is required for selected vessel options";
          }
        }

        return true;
      },
    });

    register("receiver", {
      validate: (value) => {
        if (!value || value.length === 0) {
          return "At least one receiver option is required";
        }
        return true;
      },
    });
  }, [register, optionsData]);

  // Watch vessel and receiver arrays to manage state
  const watchedVessel = watch("vessel");
  const watchedReceiver = watch("receiver");

  // Group options by category
  const groupedOptions = React.useMemo(() => {
    if (!optionsData) return {};

    return optionsData.reduce((acc, option) => {
      const category = option.category?.toLowerCase();
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(option);
      return acc;
    }, {});
  }, [optionsData]);

  // Helper function to check if an option is selected in vessel/receiver
  const isOptionSelected = (optionId, fieldArray) => {
    return fieldArray.some((item) => item.optionId === optionId);
  };

  // Helper function to get quantity for an option
  const getQuantity = (optionId, fieldArray) => {
    const item = fieldArray.find((item) => item.optionId === optionId);
    return item?.quantity || "";
  };

  // Handle vessel checkbox change
  const handleVesselChange = (optionId, isChecked) => {
    const currentVessel = watchedVessel || [];
    let newVessel;

    if (isChecked) {
      newVessel = [...currentVessel, { optionId, quantity: "" }];
    } else {
      newVessel = currentVessel.filter((item) => item.optionId !== optionId);
    }

    setValue("vessel", newVessel);
    setTimeout(() => triggerValidation("vessel"), 0);
  };

  // Handle vessel quantity change
  const handleVesselQuantityChange = (optionId, quantity) => {
    const currentVessel = watchedVessel || [];
    const updatedVessel = currentVessel.map((item) =>
      item.optionId === optionId ? { ...item, quantity: quantity } : item
    );
    setValue("vessel", updatedVessel);
    setTimeout(() => triggerValidation("vessel"), 0);
  };

  // Handle receiver checkbox change
  const handleReceiverChange = (optionId, isChecked) => {
    const currentReceiver = watchedReceiver || [];
    let newReceiver;

    if (isChecked) {
      newReceiver = [...currentReceiver, { optionId, quantity: "" }];
    } else {
      newReceiver = currentReceiver.filter(
        (item) => item.optionId !== optionId
      );
    }

    setValue("receiver", newReceiver);
    setTimeout(() => triggerValidation("receiver"), 0);
  };

  // Handle receiver quantity change
  const handleReceiverQuantityChange = (optionId, quantity) => {
    const currentReceiver = watchedReceiver || [];
    const updatedReceiver = currentReceiver.map((item) =>
      item.optionId === optionId ? { ...item, quantity: quantity } : item
    );
    setValue("receiver", updatedReceiver);
    setTimeout(() => triggerValidation("receiver"), 0);
  };

  // Check if option should show quantity input
  const shouldShowQuantity = (optionName, category) => {
    if (category === "vessel") {
      return optionName?.toLowerCase().includes("d/l");
    }
    if (category === "receiver") {
      return (
        optionName?.toLowerCase().includes("street") ||
        optionName?.toLowerCase().includes("neighbours")
      );
    }
    return false;
  };

  const submitForm = (data) => {
    const formattedData = {
      trigger: data?.trigger || "",
      type: data?.type || "",
      vessel: data?.vessel || [],
      message: data?.message || "",
      prompt: data?.prompt || "",
      receiver: data?.receiver || [],
      expiry: data?.expiry ? Number(data.expiry) : 0,
    };

    onSubmit(formattedData);
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading options: {error?.message}</div>;

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4 p-2">
      <div>
        <label htmlFor="trigger" className="form-label">
          Trigger
        </label>
        <input
          type="text"
          id="trigger"
          {...register("trigger", { required: true })}
          placeholder="Enter trigger"
          className={`form-input border ${
            errors.trigger ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.trigger && (
          <p className="form-error-message">Trigger is required</p>
        )}
      </div>

      <div>
        <label htmlFor="type" className="form-label">
          Type
        </label>
        <div className="flex gap-4">
          {groupedOptions.type?.map((option, index) => (
            <label key={index} className="inline-flex items-center space-x-1">
              <input
                type="radio"
                value={option._id}
                {...register("type", { required: true })}
                className="form-radio"
              />
              <span>{option.name}</span>
            </label>
          ))}
        </div>
        {errors.type && <p className="form-error-message">Type is required</p>}
      </div>

      <div>
        <label htmlFor="vessel" className="form-label">
          Vessel
        </label>

        {groupedOptions.vessel?.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {groupedOptions.vessel.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isOptionSelected(option._id, watchedVessel || [])}
                    onChange={(e) =>
                      handleVesselChange(option._id, e.target.checked)
                    }
                    className="form-checkbox"
                  />
                  <span>{option.name}</span>
                </label>

                {shouldShowQuantity(option.name, "vessel") &&
                  isOptionSelected(option._id, watchedVessel || []) && (
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={getQuantity(option._id, watchedVessel || [])}
                      onChange={(e) =>
                        handleVesselQuantityChange(option._id, e.target.value)
                      }
                      className="form-input border border-mediumgray w-24 ml-2"
                      min="0"
                    />
                  )}
              </div>
            ))}
          </div>
        )}

        {errors.vessel && (
          <p className="form-error-message">{errors.vessel.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="message" className="form-label">
          Message
        </label>
        <textarea
          id="message"
          {...register("message", { required: true })}
          placeholder="Enter message"
          rows={3}
          className={`form-input border ${
            errors.message ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.message && (
          <p className="form-error-message">Message is required</p>
        )}
      </div>

      <div>
        <label htmlFor="prompt" className="form-label">
          Prompt
        </label>
        <textarea
          id="prompt"
          {...register("prompt", { required: true })}
          placeholder="Enter prompt"
          rows={3}
          className={`form-input border ${
            errors.prompt ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.prompt && (
          <p className="form-error-message">Prompt is required</p>
        )}
      </div>

      <div>
        <label htmlFor="receiver" className="form-label">
          Receiver
        </label>

        {groupedOptions.receiver?.length > 0 && (
          <div className="grid grid-cols-1 gap-2">
            {groupedOptions.receiver.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isOptionSelected(
                      option._id,
                      watchedReceiver || []
                    )}
                    onChange={(e) =>
                      handleReceiverChange(option._id, e.target.checked)
                    }
                    className="form-checkbox"
                  />
                  <span>{option.name}</span>
                </label>

                {shouldShowQuantity(option.name, "receiver") &&
                  isOptionSelected(option._id, watchedReceiver || []) && (
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={getQuantity(option._id, watchedReceiver || [])}
                      onChange={(e) =>
                        handleReceiverQuantityChange(option._id, e.target.value)
                      }
                      className="form-input border border-mediumgray w-24 ml-2"
                      min="0"
                    />
                  )}
              </div>
            ))}
          </div>
        )}

        {errors.receiver && (
          <p className="form-error-message">{errors.receiver.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="expiry" className="form-label">
          Expiry (Days)
        </label>
        <input
          type="number"
          id="expiry"
          {...register("expiry", { required: true, min: 0 })}
          placeholder="Enter number of days"
          min="0"
          className={`form-input border ${
            errors.expiry ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.expiry && (
          <p className="form-error-message">Expiry days is required</p>
        )}
      </div>

      <Button type="submit" className="black-button">
        Save
      </Button>
    </form>
  );
};

export default TemplateEdit;